// hooks/useNotifications.ts
//
// One hook that owns:
//   * Initial fetch via /api/notifications (server-side route → RLS scoped SELECT)
//   * Realtime postgres_changes subscription scoped to user_id = auth.uid()
//     (RLS still scopes the payload server-side; client filter is for efficiency)
//   * Mark-read + mark-all-read mutations via /api/notifications/mark-*
//   * Splits into unread/read lists, exposes unreadCount
//
// Mutations apply optimistic updates: mark-read flips is_read=true in the
// React Query cache immediately (cancel-in-flight, snapshot, optimistically
// update; rollback in onError; invalidate in onSettled). This avoids a
// loading flash when the user clicks the bell — the count drops instantly
// and the server confirms in the background.
//
// On WebSocket reconnect, the React Query client refetches the initial
// list automatically (refetchOnReconnect: true), so missed events are
// reconciled without manual lastEventId tracking.

'use client'

import { useEffect, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useAuth } from '@/lib/auth/AuthProvider'
import { subscribeToNotificationInvalidations } from '@/lib/supabase/browserRealtime'
import { type Notification, asNotification, type NotificationRow } from '@/lib/types/notifications'

const notificationKey = (userId: string | null) => ['notifications', userId] as const

function isActiveNotificationAt(
  notification: { expires_at: string | null },
  now: number
): boolean {
  return !notification.expires_at || Date.parse(notification.expires_at) > now
}

async function fetchNotifications(): Promise<Notification[]> {
  const res = await fetch('/api/notifications?limit=100', { cache: 'no-store' })
  if (!res.ok) throw new Error(`HTTP ${res.status}`)
  const json = await res.json()
  const now = Date.now()
  return ((json.notifications ?? []) as Notification[]).filter((notification) =>
    isActiveNotificationAt(notification, now)
  )
}

export function useNotifications() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => notificationKey(user?.id ?? null), [user?.id])

  const query = useQuery({
    queryKey,
    queryFn: fetchNotifications,
    enabled: !!user?.id,
    staleTime: 30_000,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    refetchOnReconnect: true
  })

  // Realtime subscription
  //
  // Channel-lifecycle hardening (29 May 2026):
  //   @supabase/realtime-js keeps a registry of channels keyed by topic.
  //   If `supabase.channel(topic)` is called for a topic that already
  //   exists in the registry, it returns the EXISTING channel object —
  //   and `.on('postgres_changes', ...)` on a channel where `.subscribe()`
  //   was already fired throws:
  //       "cannot add `postgres_changes` callbacks ... after `subscribe()`."
  //
  //   `removeChannel()` is asynchronous (sends UNSUBSCRIBE over the WS and
  //   only fully removes the channel after the server ACK, ~hundreds of
  //   ms). When this hook unmounts → remounts faster than that round-trip
  //   (auth state churn, React 18 Suspense, realtime tenant restart →
  //   forced reconnect), the new mount hits the stale channel and bombs.
  //
  //   A unique channel name per mount via crypto.randomUUID() guarantees a
  //   fresh registry slot. The bell and deadline banner may both subscribe;
  //   each removes only its own channel on unmount.
  useEffect(() => {
    if (!user?.id) return

    return subscribeToNotificationInvalidations({
      userId: user.id,
      nonce: crypto.randomUUID(),
      onInsert: (payload) => {
        const row = payload.new as NotificationRow
        if (!isActiveNotificationAt(row, Date.now())) return
        queryClient.setQueryData<Notification[]>(queryKey, (prev) => {
          const existing = prev ?? []
          if (existing.some((notification) => notification.id === row.id)) return existing
          return [asNotification(row), ...existing]
        })
      },
      onUpdate: (payload) => {
        const row = payload.new as NotificationRow
        queryClient.setQueryData<Notification[]>(queryKey, (prev) => {
          if (!prev) return prev
          if (!isActiveNotificationAt(row, Date.now())) {
            return prev.filter((notification) => notification.id !== row.id)
          }
          return prev.map((notification) =>
            notification.id === row.id ? asNotification(row) : notification
          )
        })
      }
    })
  }, [user?.id, queryClient, queryKey])

  const markRead = useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch('/api/notifications/mark-read', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ids })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()).updated as number
    },
    onMutate: async (ids: string[]) => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Notification[]>(queryKey)
      const idSet = new Set(ids)
      queryClient.setQueryData<Notification[]>(queryKey, (prev) =>
        (prev ?? []).map((n) => (idSet.has(n.id) ? { ...n, is_read: true } : n))
      )
      return { previous, queryKey }
    },
    onError: (_err, _ids, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ctx.queryKey, ctx.previous)
    },
    onSettled: (_data, _error, _ids, ctx) => {
      if (ctx) return queryClient.invalidateQueries({ queryKey: ctx.queryKey })
    }
  })

  const markAllRead = useMutation({
    mutationFn: async () => {
      const res = await fetch('/api/notifications/mark-all-read', { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return (await res.json()).updated as number
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey })
      const previous = queryClient.getQueryData<Notification[]>(queryKey)
      queryClient.setQueryData<Notification[]>(queryKey, (prev) =>
        (prev ?? []).map((n) => ({ ...n, is_read: true }))
      )
      return { previous, queryKey }
    },
    onError: (_err, _v, ctx) => {
      if (ctx?.previous) queryClient.setQueryData(ctx.queryKey, ctx.previous)
    },
    onSettled: (_data, _error, _value, ctx) => {
      if (ctx) return queryClient.invalidateQueries({ queryKey: ctx.queryKey })
    }
  })

  const all = query.data ?? []
  const unread = all.filter((n) => !n.is_read)
  const read = all.filter((n) => n.is_read)

  return {
    all,
    unread,
    read,
    unreadCount: unread.length,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    error: query.error,
    refetch: query.refetch,
    markRead: (ids: string[]) => markRead.mutateAsync(ids),
    markAllRead: () => markAllRead.mutateAsync(),
    markReadPending: markRead.isPending,
    markAllReadPending: markAllRead.isPending
  }
}
